<template>
	<v-dialog :model-value="modelValue" @update:model-value="handleClose" width="1440px" height="920px" persistent class="deleted-duties-dialog">
		<v-card>
			<v-card-title class="dialog-header px-4">
				<span class="dialog-title">{{ $t('planning_deleted_duties') }}</span>
				<v-btn icon variant="text" size="small" @click="handleClose(false)">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<v-divider />

			<div class="toolbar px-4">
				<v-row align="center" no-gutters class="gap-2">
					<v-col cols="3">
						<v-text-field
							v-model="searchText"
							@update:model-value="handleSearchInput"
							@click:clear="handleSearchInput('')"
							append-inner-icon="mdi-magnify"
							density="compact"
							:label="$t('action_search')"
							variant="outlined"
							clearable
							hide-details
							class="search-field"
						/>
					</v-col>
					<v-spacer />
					<v-col cols="auto">
						<DateRangePicker
							v-model="placeholderDateRange"
							:published-range="dummyRange"
							:planning-range="dummyRange"
							:hide-sidebar="true"
							@update:model-value="handleDateRangeChange"
							@change="handleDateRangeChange"
						/>
					</v-col>
				</v-row>
			</div>

			<v-card-text class="pa-0">
				<div class="table-container" @scroll="handleInfiniteScroll">
					<table>
						<thead>
							<tr>
								<th class="col-duty">{{ $t('duty_duty') }}</th>
								<th class="col-date">{{ $t('duty_day') }}</th>
								<th class="col-time">{{ $t('duty_shift_start') }}</th>
								<th class="col-time">{{ $t('duty_shift_end') }}</th>
								<th class="col-user">{{ $t('planning_disposition') }}</th>
								<th class="col-user">{{ $t('duty_deleted_by') }}</th>
								<th class="col-actions">{{ $t('adapter_manage_actions') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="duty in filteredDuties" :key="duty.id">
								<td>{{ duty.name }}</td>
								<td>{{ formatDate(duty.day?.date) }}</td>
								<td>{{ secondsToTime(duty.start) }}</td>
								<td>{{ secondsToTime(duty.end) }}</td>
								<td>{{ resolveUserDisplayName(duty.user) }}</td>
								<td>{{ resolveUserDisplayName(duty.deletedBy) }}</td>
								<td class="actions-cell">
									<IconButton
										icon="mdi-delete-restore"
										iconColor="grey"
										buttonSize="32"
										size="20"
										:disabled="store.loading"
										@click="handleRestore(duty.id)"
									/>
								</td>
							</tr>
							<tr v-if="!store.loading && filteredDuties.length === 0">
								<td colspan="7" class="empty-state">
									{{ $t('no_data') }}
								</td>
							</tr>
						</tbody>
					</table>
					<div v-if="store.loading" class="d-flex justify-center py-3">
						<v-progress-circular size="24" indeterminate color="primary" />
					</div>
				</div>
			</v-card-text>
		</v-card>

		<v-dialog v-model="showConfirmDialog" width="776" persistent>
			<v-card class="confirm-card">
				<v-card-text class="confirm-text text-black text-h1">
					{{ $t('planning_confirm_restore_duty') }}
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn variant="text" @click="cancelRestore">{{ $t('action_cancel') }}</v-btn>
					<v-btn variant="flat" color="primary-green" :loading="store.loading" @click="confirmRestore">
						{{ $t('action_undelete') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-snackbar v-model="showToast" color="success" location="bottom center" :timeout="3000" content-class="restore-toast">
			<div class="d-flex align-center" style="gap: 16px">
				<v-icon>mdi-check-circle</v-icon>
				{{ $t('duty_restored_successfully') }}
			</div>
			<template #actions>
				<v-btn icon variant="text" size="small" @click="showToast = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</template>
		</v-snackbar>
	</v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { DateTime } from 'luxon';

import DateRangePicker from '@/rennovation/shared/components/DateRangePicker.vue';
import IconButton from '@/rennovation/planning-view/components/Controls/IconButton.vue';
import type { DateRange } from '@/rennovation/shared/types/date-range-picker';
import { useDeletedDuties } from '@/rennovation/planning-view/composables/useDeletedDuties';
import { secondsToTime, formatShortDate } from '@/rennovation/shared/utils/date-utils';
import useDateTime from '../../../../../../components/composables/useDateTime';
import { resolveUserDisplayName } from '@/rennovation/shared/utils/user-utils';

const props = defineProps<{ modelValue: boolean }>();
const emit = defineEmits<{ (e: 'update:modelValue', value: boolean): void }>();
const { searchText, filteredDuties, handleInfiniteScroll, handleSearchInput, initialize, store } = useDeletedDuties();

const placeholderDateRange = ref<DateRange>({ startDate: null, endDate: null });
const dummyRange = computed<DateRange>(() => ({ startDate: null, endDate: null }));
const showToast = ref(false);
const showConfirmDialog = ref(false);
const pendingRestoreId = ref<string | null>(null);

const { formatDate } = useDateTime();

async function handleDateRangeChange(range: DateRange): Promise<void> {
	const from = range.startDate ? DateTime.fromJSDate(range.startDate as Date).toISODate() : null;
	const to = range.endDate ? DateTime.fromJSDate(range.endDate as Date).toISODate() : null;

	store.setFilters(from, to);
	await store.loadChunk();
}

function handleRestore(dutyId: string): void {
	pendingRestoreId.value = dutyId;
	showConfirmDialog.value = true;
}

async function confirmRestore(): Promise<void> {
	if (!pendingRestoreId.value) return;

	try {
		await store.restore(pendingRestoreId.value);

		showConfirmDialog.value = false;
		pendingRestoreId.value = null;
		showToast.value = true;
	} catch (err) {
		console.error('Restore failed', err);
	}
}

function cancelRestore(): void {
	showConfirmDialog.value = false;
	pendingRestoreId.value = null;
}

function handleClose(value: boolean): void {
	placeholderDateRange.value = { startDate: null, endDate: null };
	store.reset();
	emit('update:modelValue', value);
}

watch(
	() => props.modelValue,
	async (val) => {
		if (val) {
			await initialize();
		}
	}
);
</script>

<style scoped>
.dialog-title {
	font-family: Roboto, sans-serif;
	font-weight: 400;
	font-size: 20px;
	line-height: 32px;
	letter-spacing: 0.25px;
	color: #000000fa;
	vertical-align: middle;
}
.dialog-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 80px;
	min-height: 80px;
	max-height: 80px;
	background-color: #ffffff;
	padding-top: 0;
	padding-bottom: 0;
}

.toolbar {
	padding-top: 8px;
	padding-bottom: 16px;
}

.search-field :deep(.v-field__input) {
	min-height: 32px;
	font-size: 0.75rem;
}

.table-container {
	flex: 1;
	overflow-y: auto;
	max-height: 660px;
	border-top: 1px solid #e0e0e0;
	border-left: 1px solid #e0e0e0;
	border-right: 1px solid #e0e0e0;
	border-bottom: 1px solid #e0e0e0;
	border-radius: 8px;
}

table {
	width: 100%;
	border-collapse: collapse;
	table-layout: fixed;
}

.col-duty {
	width: 150px;
}

.col-date {
	width: 120px;
}

.col-time {
	width: 100px;
}

.col-user {
	width: 200px;
}

.col-actions {
	width: 60px;
}

thead {
	position: sticky;
	top: 0;
	z-index: 2;
	background-color: white;
	box-shadow: inset 0 -1px 0 0 #eeeeee;
}

th,
td {
	padding: 8px 12px;
	text-align: left;
	white-space: normal;
	word-break: break-word;
	vertical-align: top;
	color: #000000;
}

tbody > tr {
	border-top: 1px solid #eeeeee;
}

tbody > tr:hover {
	background-color: #f5f5f5;
}

.actions-cell {
	text-align: center;
}

.empty-state {
	text-align: center;
	padding: 24px;
	color: rgba(0, 0, 0, 0.38);
}

.confirm-card {
	width: 776px;
	height: 133px;
	min-height: 133px;
	max-height: 133px;
	border-radius: 4px !important;
	border: 1px solid #f5f5f5 !important;
	padding: 20px !important;
	gap: 10px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
}

.confirm-card .v-card-text,
.confirm-card .v-card-actions {
	padding: 0 !important;
}
</style>

<style>
.deleted-duties-dialog .v-overlay__content {
	max-height: 90vh !important;
	width: 65vw !important;
	display: flex;
	flex-direction: column;
}

.deleted-duties-dialog .v-overlay__content .v-card {
	height: 100%;
	display: flex;
	flex-direction: column;
	border-radius: 6px !important;
}

.restore-toast {
	width: 494px !important;
	min-width: 494px !important;
	height: 56px !important;
	min-height: 56px !important;
	max-height: 56px !important;
	border-radius: 4px !important;
	padding: 16px !important;
	opacity: 1;
	display: flex;
	align-items: center;
	gap: 16px;
}

.deleted-duties-dialog .v-overlay__content .v-card-text {
	flex: 1;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	padding: 0 16px !important;
}

.deleted-duties-dialog .v-overlay__content .v-card-text.confirm-text {
	color: #000000 !important;
	opacity: 1 !important;
}

.deleted-duties-dialog .dropdown-panel {
	top: calc(100% + 4px) !important;
	left: auto !important;
	right: 0 !important;
}

.deleted-duties-dialog .dropdown-panel.is-disposition {
	top: calc(100% + 4px) !important;
	left: auto !important;
	right: 0 !important;
}
</style>
